import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import Avatar from '@material-ui/core/Avatar'
import Button from '@material-ui/core/Button'
import Drawer from '@material-ui/core/Drawer'
import IconButton from '@material-ui/core/IconButton'
import Tooltip from '@material-ui/core/Tooltip'
import Typography from '@material-ui/core/Typography'
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles'
import CloseIcon from '@material-ui/icons/Close'
import SendIcon from '@material-ui/icons/Send'
import { NestAPI, sessionContext } from '@marketing-milk/frontend'
import { Comment, CommentAnchorType } from '@marketing-milk/interfaces'
import { formatDistance, format as dateFormat } from 'date-fns'
import { ContentEditableInput } from 'components/mentions/ContentEditableInput'
import { MentionSuggestions } from 'components/mentions/MentionSuggestions'
import { useMentions } from 'hooks/useMentions'
import { useSlackUsers, SlackUser } from 'hooks/useSlackUsers'
import { useComments } from 'hooks/api/useComments'

export interface CommentAnchor {
  type: CommentAnchorType
  id: string
  label?: string
}

export interface CommentNotificationContext {
  header: string
  pageUrlBase: string
  /**
   * Optional per-anchor override of the deep-link base. Hosts with multiple
   * anchor types (e.g. the Sprint Packet) use this to route an anchor's comment
   * links somewhere other than the page they were written on — the audience list
   * for an `audience`. The comment params are appended to whatever it returns, so
   * the base must not carry a search of its own. Falls back to `pageUrlBase` when
   * omitted or when it returns undefined.
   */
  pageUrlBaseForAnchor?: (anchor: CommentAnchor) => string | undefined
  /**
   * Optional per-anchor override of the whole deep-link, including its params.
   * `pageUrlBaseForAnchor` can't express a destination that needs a param of its
   * own — the base's search component is replaced with the comment params below —
   * and the packet's review surface needs `?ad=` alongside them to open the right
   * ad. Receives the id of the comment just created; falls back to the base-URL
   * behavior when omitted or when it returns undefined.
   */
  pageUrlForAnchor?: (anchor: CommentAnchor, commentId: number) => string | undefined
  /**
   * The business's Slack channel id, if configured. When present, every comment
   * (tagged or not) is also posted as a top-level message to that channel; tagged
   * users still receive DMs. Omitted/null preserves the legacy DM-only behavior.
   */
  slackChannelId?: string | null
}

interface Props {
  open: boolean
  onClose: () => void
  businessID: number
  anchor: CommentAnchor | null
  notificationContext?: CommentNotificationContext
  /**
   * Fired after a comment is created, resolved/unresolved, or removed — i.e. any
   * change that can shift an anchor's unresolved-comment count. The thread's own
   * `useComments` query already refreshes; hosts that derive badge counts from a
   * separate source (the packet's hydrated comments) use this to re-sync them.
   */
  onCommentsChanged?: () => void
  /**
   * In-app comment selection (v1.4b "All comments" list): scroll + highlight this
   * comment. Preferred over the URL `?commentId=` deep-link so selecting from the
   * list doesn't rewrite the URL. A resolved target auto-reveals resolved roots,
   * and re-selecting a different comment on the same open anchor re-fires.
   */
  targetCommentId?: number | null
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    // theme.zIndex.modal (1300) + 1: the thread is the topmost transient surface
    // and must float above a peer modal it can be opened over — notably the native
    // audience ViewAudienceModal (a v4 Dialog at 1300, portaled later in the DOM,
    // which would otherwise cover an equal-z-index thread). The dialog must also
    // pass disableEnforceFocus so the thread's inputs stay typeable.
    drawerPaper: { zIndex: theme.zIndex.modal + 1 },
    root: {
      width: 450,
      padding: theme.spacing(3),
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    list: {
      flex: 1,
      overflowY: 'auto',
      marginTop: theme.spacing(2),
    },
    item: {
      padding: theme.spacing(2),
      backgroundColor: theme.palette.grey[100],
      borderRadius: theme.spacing(1.5),
      display: 'flex',
      gap: theme.spacing(2),
      border: '2px solid transparent',
      transition: 'border-color 0.3s ease-in-out',
      marginBottom: theme.spacing(1.5),
    },
    itemReply: {
      marginLeft: theme.spacing(5),
      backgroundColor: theme.palette.grey[50],
    },
    itemResolved: {
      opacity: 0.6,
    },
    itemOwn: {
      backgroundColor: theme.palette.primary.main,
      '& $author, & $time, & $text': { color: theme.palette.primary.contrastText },
      '& $time': { opacity: 0.8 },
    },
    itemHighlighted: {
      animation: '$highlight 2s ease-in-out',
    },
    '@keyframes highlight': {
      '0%': { borderColor: 'transparent' },
      '10%': { borderColor: theme.palette.info.main },
      '90%': { borderColor: theme.palette.info.main },
      '100%': { borderColor: 'transparent' },
    },
    avatar: { width: theme.spacing(5), height: theme.spacing(5), fontSize: '0.95rem' },
    content: { flex: 1, minWidth: 0 },
    author: { fontWeight: 'bold', fontSize: '0.9rem', marginBottom: theme.spacing(0.5) },
    time: { fontSize: '0.8rem', color: theme.palette.grey[600], marginBottom: theme.spacing(1) },
    text: { fontSize: '0.95rem', lineHeight: 1.5, wordBreak: 'break-word', whiteSpace: 'pre-wrap' },
    actions: {
      display: 'flex',
      gap: theme.spacing(1),
      marginTop: theme.spacing(0.5),
      fontSize: '0.75rem',
    },
    editBox: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(0.5),
    },
    actionBtn: {
      background: 'none',
      border: 'none',
      padding: 0,
      cursor: 'pointer',
      color: 'inherit',
      opacity: 0.7,
      fontSize: '0.75rem',
      '&:hover': { opacity: 1, textDecoration: 'underline' },
    },
    composer: {
      marginTop: theme.spacing(2),
      paddingTop: theme.spacing(2),
      borderTop: `1px solid ${theme.palette.grey[300]}`,
      display: 'flex',
      gap: theme.spacing(1),
      position: 'relative',
    },
    replyingTo: {
      fontSize: '0.8rem',
      color: theme.palette.grey[600],
      marginBottom: theme.spacing(1),
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    toggleResolved: {
      marginTop: theme.spacing(1),
      fontSize: '0.85rem',
    },
  })
)

const deletedAuthorInitials = 'DU'

const isCurrentUser = (sessionEmail: string | undefined, c: Comment) =>
  Boolean(sessionEmail && c.author?.email && sessionEmail === c.author.email)

const formatRelative = (iso: string) =>
  formatDistance(new Date(iso), new Date(), { addSuffix: true })

const formatFull = (iso: string) => dateFormat(new Date(iso), 'PPpp')

export function CommentThread({
  open,
  onClose,
  businessID,
  anchor,
  notificationContext,
  onCommentsChanged,
  targetCommentId,
}: Props) {
  const classes = useStyles()
  const { user } = useContext(sessionContext)
  const { users: slackUsers } = useSlackUsers()

  const { comments, create, update, remove, resolve, unresolve } = useComments(
    businessID,
    anchor?.type ?? 'section',
    anchor?.id ?? null
  )

  const [text, setText] = useState('')
  const [cursorPos, setCursorPos] = useState(0)
  const [controlledCursorPos, setControlledCursorPos] = useState<number | undefined>(undefined)
  const [replyingTo, setReplyingTo] = useState<number | null>(null)
  const [showResolved, setShowResolved] = useState(false)
  const [highlightedID, setHighlightedID] = useState<number | null>(null)
  // Inline edit state is kept separate from the composer's `text`/`taggedUsers`
  // so editing a posted comment can't collide with a half-typed new comment.
  const [editingID, setEditingID] = useState<number | null>(null)
  const [editText, setEditText] = useState('')

  const {
    isShowingSuggestions,
    filteredUsers,
    selectedIndex,
    selectUser,
    handleKeyDown,
    taggedUsers,
    clearTaggedUsers,
  } = useMentions(text, cursorPos, slackUsers)

  const listRef = useRef<HTMLDivElement>(null)
  const commentRefs = useRef<Record<number, HTMLDivElement | null>>({})
  const hasScrolledRef = useRef(false)
  const hasScrolledToSpecificRef = useRef(false)

  // Drain state whenever the drawer closes.
  useEffect(() => {
    if (!open) {
      hasScrolledRef.current = false
      hasScrolledToSpecificRef.current = false
      setReplyingTo(null)
      setText('')
      setCursorPos(0)
      setEditingID(null)
      setEditText('')
      clearTaggedUsers()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  // Drain draft + scroll flags when the anchor itself changes, so switching
  // between section/KPI comment triggers with the drawer open doesn't carry
  // stale draft text or reply target into the new thread.
  useEffect(() => {
    hasScrolledRef.current = false
    hasScrolledToSpecificRef.current = false
    setReplyingTo(null)
    setText('')
    setCursorPos(0)
    setEditingID(null)
    setEditText('')
    clearTaggedUsers()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anchor?.type, anchor?.id])

  // ?commentId= deep-link: scroll + highlight. Skipped when an in-app
  // `targetCommentId` is active — that prop drives its own effect below.
  useEffect(() => {
    if (!open || hasScrolledRef.current || typeof targetCommentId === 'number') return
    const urlParams = new URLSearchParams(window.location.search)
    const commentId = urlParams.get('commentId')
    const timer = setTimeout(() => {
      if (commentId && commentRefs.current[Number(commentId)]) {
        commentRefs.current[Number(commentId)]?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        })
        setHighlightedID(Number(commentId))
        setTimeout(() => setHighlightedID(null), 2000)
        hasScrolledRef.current = true
        hasScrolledToSpecificRef.current = true
      } else if (comments.length > 0 && !commentId) {
        const last = comments[comments.length - 1]
        commentRefs.current[last.id]?.scrollIntoView({ behavior: 'auto', block: 'end' })
        hasScrolledRef.current = true
      }
    }, 400)
    return () => clearTimeout(timer)
  }, [open, comments, targetCommentId])

  // Prop-driven highlight (in-app selection from the "All comments" list). A
  // separate effect keyed on `targetCommentId` — not gated by the one-shot
  // `hasScrolledRef` — so re-selecting a different comment on an already-open
  // anchor re-scrolls/re-highlights.
  useEffect(() => {
    if (!open || typeof targetCommentId !== 'number') return
    const target = comments.find(c => c.id === targetCommentId)
    // Resolved roots are hidden unless `showResolved` is on; reveal so a resolved
    // target selected from the list is actually shown and highlighted.
    if (target?.resolved) setShowResolved(true)
    let clearTimer: ReturnType<typeof setTimeout>
    const timer = setTimeout(() => {
      const el = commentRefs.current[targetCommentId]
      if (!el) return
      el.scrollIntoView({ behavior: 'smooth', block: 'center' })
      setHighlightedID(targetCommentId)
      hasScrolledRef.current = true
      hasScrolledToSpecificRef.current = true
      clearTimer = setTimeout(() => setHighlightedID(null), 2000)
    }, 400)
    return () => {
      clearTimeout(timer)
      clearTimeout(clearTimer)
    }
  }, [open, targetCommentId, comments])

  // Auto-scroll to new comments unless we landed on a deep-link
  useEffect(() => {
    if (
      open &&
      hasScrolledRef.current &&
      !hasScrolledToSpecificRef.current &&
      comments.length > 0
    ) {
      const last = comments[comments.length - 1]
      setTimeout(() => {
        commentRefs.current[last.id]?.scrollIntoView({ behavior: 'smooth', block: 'end' })
      }, 100)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comments.length, open])

  // Reset controlled cursor after use
  useEffect(() => {
    if (controlledCursorPos !== undefined) {
      const timer = setTimeout(() => setControlledCursorPos(undefined), 50)
      return () => clearTimeout(timer)
    }
  }, [controlledCursorPos])

  const { roots, repliesByParent } = useMemo(() => {
    const sorted = [...comments].sort((a, b) => a.id - b.id)
    const r: Comment[] = []
    const map: Record<number, Comment[]> = {}
    for (const c of sorted) {
      if (c.parentCommentID === null || c.parentCommentID === undefined) r.push(c)
      else (map[c.parentCommentID] ||= []).push(c)
    }
    return { roots: r, repliesByParent: map }
  }, [comments])

  const visibleRoots = showResolved ? roots : roots.filter(r => !r.resolved)
  const resolvedCount = roots.length - roots.filter(r => !r.resolved).length

  const handleClose = () => {
    onClose()
    const urlParams = new URLSearchParams(window.location.search)
    if (urlParams.has('commentId')) {
      window.history.replaceState({}, '', window.location.pathname)
    }
  }

  const handleSend = async () => {
    if (!text.trim() || !anchor) return
    try {
      const taggedUserIDs = taggedUsers
        .map((u: SlackUser) => Number(u.id))
        .filter(n => Number.isFinite(n))
      const created = await create({
        body: text,
        parentCommentID: replyingTo,
        taggedUserIDs: taggedUserIDs.length ? taggedUserIDs : undefined,
      })
      if (created) onCommentsChanged?.()

      if (
        created &&
        notificationContext &&
        (taggedUsers.length > 0 || notificationContext.slackChannelId)
      ) {
        try {
          // Carry the anchor in the deep-link so packet hosts (which have many
          // anchors, unlike CreativeWizard's single one) can open the right
          // thread — and in Present mode jump to the matching slide.
          const linkParams = new URLSearchParams({
            commentId: String(created.id),
            anchorType: anchor.type,
            anchorId: anchor.id,
          })
          // Build the link via a URL object so the params always land in the
          // search component, never the fragment. Packet hosts write section
          // hashes (e.g. /edit#feedback), and string-concatenating `?...` after
          // a hash would bury the params in the fragment where
          // window.location.search can't see them.
          const fullUrl = notificationContext.pageUrlForAnchor?.(anchor, created.id)
          const pageUrlBase =
            notificationContext.pageUrlBaseForAnchor?.(anchor) ?? notificationContext.pageUrlBase
          const url = new URL(fullUrl ?? pageUrlBase, window.location.origin)
          if (!fullUrl) {
            url.hash = ''
            url.search = linkParams.toString()
          }
          await NestAPI.post('/slack/send', {
            message: text,
            header: notificationContext.header,
            taggedUsers: taggedUsers.map((u: SlackUser) => ({
              id: u.id,
              displayName: u.displayName,
            })),
            channelId: notificationContext.slackChannelId ?? null,
            pageUrl: url.toString(),
          })
        } catch (e) {
          // Slack notification is best-effort; the comment itself persisted
          // eslint-disable-next-line no-console
          console.error('Slack notification failed:', e)
        }
      }

      hasScrolledToSpecificRef.current = false
      setText('')
      setCursorPos(0)
      setReplyingTo(null)
      clearTaggedUsers()
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Error sending comment:', e)
    }
  }

  const handleKeyDownWithEnter = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && e.shiftKey) return
    if (e.key === 'Enter' && !e.shiftKey && !isShowingSuggestions) {
      e.preventDefault()
      handleSend()
      return
    }
    const result = handleKeyDown(e)
    if (
      result &&
      typeof result === 'object' &&
      result.newValue &&
      result.newCursorPosition !== undefined
    ) {
      setText(result.newValue)
      setControlledCursorPos(result.newCursorPosition)
    }
  }

  const startEdit = (c: Comment) => {
    setEditingID(c.id)
    setEditText(c.body)
  }

  const cancelEdit = () => {
    setEditingID(null)
    setEditText('')
  }

  const handleSaveEdit = async (commentID: number) => {
    if (!editText.trim()) return
    try {
      await update(commentID, { body: editText })
      setEditingID(null)
      setEditText('')
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Error editing comment:', e)
    }
  }

  const handleResolveToggle = async (c: Comment) => {
    try {
      await (c.resolved ? unresolve(c.id) : resolve(c.id))
      onCommentsChanged?.()
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Error toggling comment resolution:', e)
    }
  }

  const handleDelete = async (c: Comment) => {
    const hasReplies = (repliesByParent[c.id] || []).length > 0
    const message = hasReplies
      ? 'Delete this comment? Replies will remain as separate comments.'
      : 'Delete this comment?'
    if (!window.confirm(message)) return
    try {
      await remove(c.id)
      onCommentsChanged?.()
      if (editingID === c.id) cancelEdit()
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Error deleting comment:', e)
    }
  }

  const renderComment = (c: Comment, isReply = false) => {
    const own = isCurrentUser(user?.email, c)
    const isEditing = editingID === c.id
    const initials =
      c.author?.firstName && c.author?.lastName
        ? `${c.author.firstName[0]}${c.author.lastName[0]}`
        : deletedAuthorInitials
    return (
      <div
        key={c.id}
        ref={el => {
          commentRefs.current[c.id] = el
        }}
        className={`${classes.item} ${isReply ? classes.itemReply : ''} ${
          own ? classes.itemOwn : ''
        } ${c.resolved ? classes.itemResolved : ''} ${
          highlightedID === c.id ? classes.itemHighlighted : ''
        }`}
      >
        <Avatar className={classes.avatar} src={c.author?.profilePicture}>
          {!c.author?.profilePicture && initials}
        </Avatar>
        <div className={classes.content}>
          <div className={classes.author}>
            {c.author ? `${c.author.firstName} ${c.author.lastName}` : 'Deleted user'}
            {own && ' (You)'}
          </div>
          <Tooltip title={formatFull(c.createdAt)} placement="top">
            <div className={classes.time}>{formatRelative(c.createdAt)}</div>
          </Tooltip>
          {isEditing ? (
            <div className={classes.editBox}>
              <ContentEditableInput
                value={editText}
                onChange={setEditText}
                onCursorPositionChange={() => undefined}
                placeholder="Edit comment..."
                taggedUsers={[]}
              />
              <div className={classes.actions}>
                <button
                  type="button"
                  className={classes.actionBtn}
                  onClick={() => handleSaveEdit(c.id)}
                  disabled={!editText.trim()}
                >
                  Save
                </button>
                <button type="button" className={classes.actionBtn} onClick={cancelEdit}>
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className={classes.text}>{c.body}</div>
              {(!isReply || own) && (
                <div className={classes.actions}>
                  {!isReply && (
                    <>
                      <button
                        type="button"
                        className={classes.actionBtn}
                        onClick={() => setReplyingTo(c.id)}
                      >
                        Reply
                      </button>
                      <button
                        type="button"
                        className={classes.actionBtn}
                        onClick={() => handleResolveToggle(c)}
                      >
                        {c.resolved ? 'Unresolve' : 'Resolve'}
                      </button>
                    </>
                  )}
                  {own && (
                    <>
                      <button
                        type="button"
                        className={classes.actionBtn}
                        onClick={() => startEdit(c)}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        className={classes.actionBtn}
                        onClick={() => handleDelete(c)}
                      >
                        Delete
                      </button>
                    </>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    )
  }

  return (
    <Drawer
      anchor="right"
      open={open}
      variant="persistent"
      hideBackdrop
      classes={{ paper: classes.drawerPaper }}
      ModalProps={{ keepMounted: true, disableEnforceFocus: true, disableAutoFocus: true }}
      onClose={handleClose}
    >
      <div className={classes.root}>
        <div className={classes.header}>
          <div>
            <Typography variant="h6" gutterBottom>
              Comments
            </Typography>
            {anchor?.label && (
              <Typography variant="body2" color="textSecondary">
                {anchor.label}
              </Typography>
            )}
          </div>
          <IconButton onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </div>

        <Typography variant="body2" color="textSecondary">
          {visibleRoots.length} comment{visibleRoots.length !== 1 ? 's' : ''}
          {resolvedCount > 0 && !showResolved && ` · ${resolvedCount} resolved hidden`}
        </Typography>
        {resolvedCount > 0 && (
          <button
            type="button"
            className={classes.actionBtn + ' ' + classes.toggleResolved}
            onClick={() => setShowResolved(s => !s)}
          >
            {showResolved ? 'Hide resolved' : `Show resolved (${resolvedCount})`}
          </button>
        )}

        <div ref={listRef} className={classes.list}>
          {visibleRoots.length === 0 && (
            <Typography variant="body2" color="textSecondary" style={{ marginTop: 16 }}>
              No comments yet. Start the conversation below.
            </Typography>
          )}
          {visibleRoots.map(root => (
            <React.Fragment key={root.id}>
              {renderComment(root)}
              {(repliesByParent[root.id] || []).map(r => renderComment(r, true))}
            </React.Fragment>
          ))}
        </div>

        {replyingTo !== null && (
          <div className={classes.replyingTo}>
            <span>Replying to comment #{replyingTo}</span>
            <button type="button" className={classes.actionBtn} onClick={() => setReplyingTo(null)}>
              Cancel
            </button>
          </div>
        )}

        <div className={classes.composer}>
          {isShowingSuggestions && (
            <MentionSuggestions
              users={filteredUsers}
              selectedIndex={selectedIndex}
              onSelect={u => {
                const result = selectUser(u)
                if (result) {
                  setText(result.newValue)
                  setControlledCursorPos(result.newCursorPosition)
                }
              }}
            />
          )}
          <ContentEditableInput
            value={text}
            onChange={setText}
            onCursorPositionChange={setCursorPos}
            cursorPosition={controlledCursorPos}
            onKeyDown={handleKeyDownWithEnter}
            placeholder={
              replyingTo !== null
                ? 'Reply... (@ to mention)'
                : 'Add a comment... (@ to mention, Enter to send)'
            }
            taggedUsers={taggedUsers}
          />
          <Button
            variant="contained"
            color="primary"
            endIcon={<SendIcon />}
            onClick={handleSend}
            disabled={!text.trim() || !anchor}
          >
            Send
          </Button>
        </div>
      </div>
    </Drawer>
  )
}
